import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import type { RiskFlag } from "@/lib/api/risk-flagging";
import { useResolveAlert } from "@/lib/api/risk-flagging";
import { useMessageContext } from "@/lib/api/admin";
import { useToast } from "@/components/ui/use-toast";
import { ContextModal } from "@/components/conversation-details/message-search/context-modal";
import { trimContext, loadDraft, saveDraft } from "./utils";
import {
  Divider,
  AlertInfo,
  FlaggedContent,
  ClosedFeedback,
  FeedbackPrompt,
  ResolveForm,
} from "./drawer-sections";

interface AlertDrawerProps {
  alert: RiskFlag;
  onClose: () => void;
}

export function AlertDrawer({ alert, onClose }: AlertDrawerProps) {
  const storageKey = `risk-flag-draft-${alert.id}`;
  const navigate = useNavigate();
  const { toast } = useToast();
  const resolveAlert = useResolveAlert();

  const [showContext, setShowContext] = useState(false);
  const draft = loadDraft(storageKey);
  const [feedbackCorrect, setFeedbackCorrect] = useState<boolean | null>(
    draft?.feedbackCorrect ?? null,
  );
  const [feedbackNote, setFeedbackNote] = useState(draft?.feedbackNote ?? "");

  const isSurvey = alert.sourceType === "survey";
  const categories = alert.categories ?? [];

  const { data: rawContextData, isLoading: isContextLoading } = useMessageContext(
    alert.mentorshipId,
    !isSurvey && showContext ? Number(alert.sourceMessageId) : 0,
  );
  const contextData = useMemo(() => trimContext(rawContextData), [rawContextData]);

  const updateFeedbackCorrect = useCallback(
    (val: boolean | null) => {
      setFeedbackCorrect(val);
      saveDraft(storageKey, val, feedbackNote);
    },
    [storageKey, feedbackNote],
  );

  const updateFeedbackNote = useCallback(
    (val: string) => {
      setFeedbackNote(val);
      saveDraft(storageKey, feedbackCorrect, val);
    },
    [storageKey, feedbackCorrect],
  );

  const handleResolve = useCallback(() => {
    if (feedbackCorrect === null) return;
    resolveAlert.mutate(
      {
        alertId: alert.id,
        feedbackCorrect,
        feedbackNote: feedbackNote.trim() || undefined,
      },
      {
        onSuccess: () => {
          localStorage.removeItem(storageKey);
          toast({ title: "Alert resolved", description: "Feedback submitted successfully" });
          onClose();
        },
        onError: () => {
          toast({
            title: "Failed to resolve",
            description: "Something went wrong, please try again",
            variant: "destructive",
          });
        },
      },
    );
  }, [alert.id, feedbackCorrect, feedbackNote, storageKey, resolveAlert, toast, onClose]);

  const renderBottomSection = () => {
    if (alert.status === "CLOSED") {
      return <ClosedFeedback alert={alert} />;
    }
    if (feedbackCorrect !== null) {
      return (
        <ResolveForm
          feedbackCorrect={feedbackCorrect}
          feedbackNote={feedbackNote}
          isPending={resolveAlert.isPending}
          onChangeCorrect={updateFeedbackCorrect}
          onChangeNote={updateFeedbackNote}
          onResolve={handleResolve}
        />
      );
    }
    return <FeedbackPrompt onSelect={updateFeedbackCorrect} />;
  };

  return (
    <>
      <div className="fixed inset-0 z-50" onClick={onClose} />

      <div className="fixed bottom-6 right-6 top-6 z-50 w-[400px] overflow-y-auto rounded-[10px] border border-[rgba(31,30,29,0.12)] bg-white font-body shadow-xl">
        <div className="flex h-full flex-col gap-4 p-6">
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <p className="text-base font-normal text-[#1f1e1d]">Risk flagged</p>
              <p className="text-xs text-[#58534c]">
                {isSurvey
                  ? "A survey response triggered this alert for review"
                  : "This message was flagged by the system for review"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="shrink-0 rounded-sm p-0.5 opacity-70 hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <Divider />
          <AlertInfo alert={alert} />
          <Divider />
          <FlaggedContent
            alert={alert}
            categories={categories}
            onViewContext={() => setShowContext(true)}
          />
          <Divider />
          {renderBottomSection()}
        </div>
      </div>

      {!isSurvey && <ContextModal
        isOpen={showContext}
        onClose={() => setShowContext(false)}
        contextData={contextData}
        isLoading={isContextLoading}
        mentorId={alert.mentorship?.mentor?.id}
        title="Conversation Context"
        subtitle={`Message flagged ${formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}`}
        contentClassName="z-[60] flex h-[774px] w-[600px] max-w-[600px] flex-col gap-0 rounded-[16px] p-0 font-body"
        scrollClassName="flex-1 overflow-y-auto px-6 py-4"
        highlightClassName="border-y border-[rgba(255,107,107,0.3)] bg-[rgba(255,107,107,0.04)] py-3 mb-3"
        targetLabel={
          categories.length > 0 ? (
            <p className="-mt-1 ml-1 flex items-center gap-1 font-body text-[11px] font-normal leading-[15.4px] text-[#FF6B6B]">
              <span>►</span> Flagged content
            </p>
          ) : undefined
        }
        autoScrollToBottom
        footerContent={
          <div className="flex w-full items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowContext(false);
                navigate({
                  to: "/mentorships/$mentorshipId",
                  params: { mentorshipId: alert.mentorshipId },
                });
              }}
              className="w-[123px] rounded-[10px]"
            >
              View chat
            </Button>
            <Button
              onClick={() => setShowContext(false)}
              className="w-[123px] rounded-[10px] bg-black text-white hover:bg-zinc-800"
            >
              Done
            </Button>
          </div>
        }
      />}
    </>
  );
}
